import { Component, OnDestroy, OnInit } from '@angular/core';
import Highcharts from 'highcharts';
import { CacheService, ImageProviderService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { groupBy, groupStatusByMachine, hexToRGB } from 'projects/common/src/app/services/utils/utilities';
import { IShift } from 'src/app/modules/core/interfaces';
import { DatePresentationPipe } from 'src/app/modules/core/pipes/date-presentation.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { IStatusGroup } from 'src/app/modules/widget/interfaces/status-group';
import { IStatusMachine } from 'src/app/modules/widget/interfaces/status-machine';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-status-by-machine',
  templateUrl: './status-by-machine.component.html',
  styleUrls: ['./status-by-machine.component.scss'],
})
export class StatusByMachineComponent extends DashboardWidgetChartComponent implements OnInit, OnDestroy {
  private positionY: number;
  private counter: number;
  public displayMessage = false;
  public showLegend: boolean;
  public legendLabels: { parentStatusName: string; statusColor: string }[] = [];

  uniqueParentStatusNames: string[];

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    private langService: LanguageService,
    private datePipe: DatePresentationPipe,
    private imageProvider: ImageProviderService,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.showLegend = false;
    this.initRequestParams();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasCompany: true,
      hasDate: true,
      hasShift: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * On init component
   */
  public ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Functino to draw view
   *
   * @param chartOptions Options
   */
  public drawView(chartOptions: any): void {
    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const date = this.parameterService.parameters.item('date').value;
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;
    const hoursTranslation = this.langService.message('hours');
    const { imageProvider } = this;

    if (chartOptions.length) {
      this.hasData = true;
      this.positionY = 130;
      this.displayMessage = false;
      // Group by machine as array
      let groupByMachine = groupBy(chartOptions, (x: IStatusMachine) => x.machineName);
      groupByMachine = Object.keys(groupByMachine).map((key) => groupByMachine[key]);

      // Get the unique parent status names
      const parentStatusArray: { parentStatusName: string; statusColor: string }[] = [];
      for (const machine of groupByMachine) {
        machine.forEach((status) => {
          if (!parentStatusArray.some((item) => item.parentStatusName === status.parentStatusName)) {
            parentStatusArray.push({
              parentStatusName: status.parentStatusName,
              statusColor: status.color,
            });
          }
        });
      }

      // Create dynamic labels
      this.legendLabels = parentStatusArray;

      this.counter = 0;

      const arrayData = [];

      for (const machine of groupByMachine) {
        const statusData = groupStatusByMachine(machine);
        const serieData = this.createSerieData(statusData, groupByMachine.length);
        arrayData.push(serieData);
      }

      const series = [];
      for (const serie of arrayData) {
        for (const detail of serie) {
          series.push(detail);
        }
      }

      const data = {
        chart: {
          plotBackgroundColor: null,
          plotBorderWidth: null,
          plotShadow: false,
          type: 'pie',
          zoomType: 'xy',
          backgroundColor: '#F8F8F8',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
        },
        subtitle: {
          text: `${this.langService.message('company')}: ${user.company.name},
              ${this.langService.message('device')}: ${this.langService.message(machineType.name)},
              ${this.langService.message('date')}: ${this.datePipe.transform(date)},
              ${this.langService.message('shift_')}: ${shift.name}`,
        },
        tooltip: {
          outside: true,
          backgroundColor: '#433e3e',
          borderColor: '#433e3e',
          borderRadius: 2,
          style: {
            color: 'white',
          },
          useHTML: true,
          formatter(): string {
            const image = imageProvider.getImage(`dashboard-${machineType.name}`, this.point.options.imageName);
            const color = this.point.options.color.replace('0.8', '1');
            const hoursTime = Number(Number(this.y) / 1000 / 60 / 60).toFixed(2);

            return `
            <div style="width: 190px; white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;">
              <div style="float: left; width: 80px">
                <div>
                  <img src="${image}" width="50">
                </div>
                <div style="margin-left: 8px; margin-top: 5px; color: #ffffff">
                  <strong>${Number(this.percentage).toFixed(2)}%</strong>
                </div>
              </div>

              <div>
                <strong>${this.key}</strong> <br>
                <span>${this.point.options.machineName}</span> <br> <br>
                <span>${hoursTime} ${hoursTranslation}</span>
              </div>
            </div>
          `;
          },
        },
        plotOptions: {
          pie: {
            shadow: false,
            cursor: 'pointer',
          },
          series: {
            states: {
              hover: {
                enabled: true,
                halo: {
                  size: 0,
                },
              },
            },
          },
        },
        series,
      };

      this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
        if (this.positionY > 150) {
          chart.setSize(this.chartTarget.nativeElement.offsetWidth, this.positionY + 280);
        }
        this.showLegend = true;

        this.addLabelsToPie(chart, shift);
      });
    } else {
      this.hasData = false;
      this.displayMessage = true;
      this.showLegend = false;
    }
  }

  /**
   * Function to return the serie data for highchart
   *
   * @param statusData Data from status grouped
   */
  private createSerieData(statusData: IStatusGroup[], length: number): any {
    const serieData = [];
    const subSerieData = [];

    if (this.counter > 0 && this.counter % 4 === 0) {
      this.positionY += 300;
      this.counter = 0;
    }

    let statusSum = 0;
    let subStatusSum = 0;
    for (const status of statusData) {
      serieData.push({
        name: status.name,
        machineName: status.machineName,
        imageName: status.imageName || 'default',
        y: status.milliseconds,
        color: hexToRGB(status.color, '0.5'),
        borderColor: status.color,
        borderWidth: 1,
        totalDifference: status.totalDifference,
      });

      statusSum += status.milliseconds;

      for (const subStatus of status.statusInside) {
        subSerieData.push({
          machineName: subStatus.machineName,
          name: subStatus.name,
          imageName: subStatus.imageName || 'default',
          y: subStatus.milliseconds,
          color: hexToRGB(status.color, '0.5'),
          borderColor: status.color,
          borderWidth: 1,
          totalDifference: status.totalDifference,
        });

        subStatusSum += subStatus.milliseconds;
      }
    }

    const currentWidth = this.chartTarget.nativeElement.offsetWidth || 1042;
    let positionX = 0;
    length = length > 4 ? 4 : length;
    if (currentWidth < 1200) {
      // currentWidth = 1200;
      positionX = (currentWidth / (length || 1)) * this.counter + 95;
    } else {
      positionX = (currentWidth / (length || 1)) * this.counter + 150;
    }

    const data = [
      {
        type: 'pie',
        data: serieData,
        // size: '55%',
        size: 230,
        innerSize: '40%',
        dataLabels: {
          enabled: true,
          formatter(): string {
            if (this.percentage > 10) {
              return `${Number(this.percentage).toFixed(0)}%`;
            }
            return '';
          },
          distance: -50,
          style: {
            color: 'white',
            borderWidth: 0,
            borderColor: 'white',
          },
        },
        center: [positionX, this.positionY],
      },
      {
        type: 'pie',
        data: subSerieData,
        size: 230,
        innerSize: '70%',
        dataLabels: {
          enabled: false,
        },
        center: [positionX, this.positionY],
      },
    ];

    this.counter++;
    return data;
  }

  /**
   * Function to add the text and machine names
   *
   * @param chart Current chart
   */
  private addLabelsToPie(chart: Highcharts.Chart, shift?: IShift): void {
    const series = chart.series as any;

    if (shift.shiftId === -1) {
      const shifts = this.parameterService.parameters.item('shift-collection').value;

      let shiftSpan = 0;
      for (const current of shifts) {
        if (current.shiftId !== 1) {
          shiftSpan += Number(current.shiftSpan);
        }
      }
      shift = {
        shiftId: shift.shiftId,
        name: shift.name,
        shiftSpan,
      };
    }
    for (let i = 0; i < series.length; i += 2) {
      const serie = (series[i] as any).userOptions;
      const machineName = `<span style="font-weight: 400; color: #433e3e">${serie.data[0].machineName}</span>`;
      const positionX = Number(series[i].options.center[0]) - (Number(String(serie.data[0].machineName).length) < 7 ? -12 : 5);
      const positionY = Number(series[i].options.center[1]);
      const timeLapsed = Number(serie.data[0].totalDifference / 1000 / 60 / 60).toFixed(2);
      const showingTranslation = this.langService.message('showing');
      const from = this.langService.message('from');

      const text = `<span style="color: #707070; font-size: 12px;">${showingTranslation}</span> <br>
                    <span style="color: #707070; font-size: 12px;">${timeLapsed} hrs</span> <br>
                    <span style="color: #707070; font-size: 12px;">${from} ${shift.shiftSpan} hrs</span>`;

      chart.renderer.text(text, series[i].options.center[0], series[i].options.center[1] + 50).add();
      chart.renderer.text(machineName, positionX, positionY + 200).add();
    }
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
